<script setup lang="ts">
const props = defineProps<{
  id: string
  range: { id: number[]; value: number[] }
  isLoading?: boolean
}>()
const emit = defineEmits<{
  (e: 'change', payload: { type: string; id: string; value: string; variant: string }): void
}>()

const { locale, t } = useI18n()
const minInput = ref(props.range.value[0])
const maxInput = ref(props.range.value[1])
const selectedRangeId = ref('')
const gaOptions = inject('gaOptions', {
  group: 'plp',
})

const rangeMinMax = computed(() => {
  return {
    rangeMin: Math.min(props.range.id[0], props.range.value[0]),
    rangeMax: Math.max(props.range.id[1], props.range.value[1]),
  }
})

const predefinedRanges = computed(() => {
  const [validationMin, validationMax] = props.range.id
  return [
    {
      id: 'under100',
      label: t('range_selector.label.bucket_one'),
      min: rangeMinMax.value.rangeMin,
      max: 100,
      selected: maxInput.value === 100 && minInput.value === rangeMinMax.value.rangeMin,
      valid: validationMin <= 100,
    },
    {
      id: '100to300',
      label: t('range_selector.label.bucket_two'),
      min: 100,
      max: 300,
      selected: minInput.value === 100 && maxInput.value === 300,
      valid: validationMax >= 100 && validationMin <= 300,
    },
    {
      id: '300to1000',
      label: t('range_selector.label.bucket_three'),
      min: 300,
      max: 1000,
      selected: minInput.value === 300 && maxInput.value === 1000,
      valid: validationMax >= 300 && validationMin <= 1000,
    },
    {
      id: '1000to5000',
      label: t('range_selector.label.bucket_four'),
      min: 1000,
      max: 5000,
      selected: minInput.value === 1000 && maxInput.value === 5000,
      valid: validationMax >= 1000 && validationMin <= 5000,
    },
    {
      id: 'over5000',
      label: t('range_selector.label.bucket_five'),
      min: 5000,
      max: rangeMinMax.value.rangeMax,
      selected: minInput.value === 5000 && maxInput.value === rangeMinMax.value.rangeMax,
      valid: validationMax >= 5000,
    },
  ]
})

const updateSelectedRadio = (min: number, max: number) => {
  const matchingRange = predefinedRanges.value.find((range) => range.min === min && range.max === max)
  selectedRangeId.value = matchingRange?.id || ''
}

const handleRangeSelect = (range: { id: string; min: number; max: number }) => {
  minInput.value = range.min
  maxInput.value = range.max
  selectedRangeId.value = range.id

  emit('change', {
    type: 'filter',
    id: props.id,
    value: `(${range.min}..${range.max})`,
    variant: 'bucket',
  })

  pushEventToDataLayer('custom.filters.radio', {
    ...gaOptions,
    action: 'select',
    name: props.id,
    label: predefinedRanges.value.find((r) => r.id === range.id)?.label || '',
    info: {
      value: `(${range.min}..${range.max})`,
    },
  })
}

const handleInputChange = () => {
  selectedRangeId.value = ''

  pushEventToDataLayer('custom.filters.input', {
    ...gaOptions,
    action: 'select',
    name: props.id,
    label: 'input',
    info: {
      value: `(${minInput.value}..${maxInput.value})`,
    },
  })
}

const handleFocusOut = (type: 'min' | 'max') => {
  selectedRangeId.value = ''
  const inputValue = type === 'min' ? minInput.value : maxInput.value
  const isNegative = inputValue < 0
  const isOutOfRange = inputValue < rangeMinMax.value.rangeMin || inputValue > rangeMinMax.value.rangeMax
  const isMinGreaterThanMax = type === 'min' ? minInput.value > maxInput.value : false
  const isMaxLessThanMin = type === 'max' ? maxInput.value < minInput.value : false
  const isEmpty = inputValue === null || inputValue === undefined || Number.isNaN(inputValue) || inputValue === 0

  if (isNegative || isOutOfRange || isMinGreaterThanMax || isMaxLessThanMin || isEmpty) {
    if (type === 'min') {
      minInput.value = rangeMinMax.value.rangeMin
    } else {
      maxInput.value = rangeMinMax.value.rangeMax
    }
  }

  emit('change', {
    type: 'filter',
    id: props.id,
    value: `(${minInput.value}..${maxInput.value})`,
    variant: 'input',
  })
}

onMounted(() => {
  const initialSelected = predefinedRanges.value.find((range) => range.selected)
  if (initialSelected) {
    selectedRangeId.value = initialSelected.id
  }
})

watch(
  () => props.range.value,
  ([min, max]) => {
    minInput.value = min
    maxInput.value = max
    updateSelectedRadio(min, max)
  },
)
</script>

<template>
  <div class="range-selector">
    <div class="range-selector__inputs-container">
      <div :class="['range-selector__input-wrapper', { 'fr-ca-format': locale === 'fr' }]">
        <Input
          v-model="minInput"
          :label="t('range_slider.label.minimum')"
          :name="`${id}-min`"
          :input-test-id="`filter-${id}-min`"
          type="number"
          :min="rangeMinMax.rangeMin"
          :max="rangeMinMax.rangeMax"
          :disabled="isLoading"
          @input="handleInputChange"
          @blur="handleFocusOut('min')"
        />
      </div>

      <div :class="['range-selector__input-wrapper', { 'fr-ca-format': locale === 'fr' }]">
        <Input
          v-model="maxInput"
          :label="t('range_slider.label.maximum')"
          :name="`${id}-max`"
          :input-test-id="`filter-${id}-max`"
          type="number"
          :min="rangeMinMax.rangeMin"
          :max="rangeMinMax.rangeMax"
          :disabled="isLoading"
          @input="handleInputChange"
          @blur="handleFocusOut('max')"
        />
      </div>
    </div>
    <div class="range-selector__radio-container">
      <Radio
        v-for="radioRange in predefinedRanges.filter((range) => range.valid)"
        :key="radioRange.id"
        :label="radioRange.label"
        v-model="selectedRangeId"
        :value="radioRange.id"
        :name="radioRange.label"
        :test-id="`filter-${id}-bucket-${radioRange.id}`"
        @select="handleRangeSelect(radioRange)"
      />
    </div>
  </div>
</template>

<style lang="postcss">
.range-selector {
  padding-block: var(--space-30);
  padding-left: var(--space-10);

  .range-selector__inputs-container {
    display: flex;
    justify-content: space-between;
    gap: var(--space-50);
    margin-bottom: var(--space-60);

    > * {
      width: 100%;
    }
  }

  .range-selector__input-wrapper {
    position: relative;
    width: 100%;

    &::before {
      content: '$';
      position: absolute;
      top: calc(50% + 0.375rem);
      left: var(--space-50);
      transform: translateY(-50%);
      z-index: 1;
      font-size: var(--space-50);
      color: var(--text-primary);
    }

    &.fr-ca-format::before {
      right: var(--space-50);
      left: auto;
    }

    input {
      &:not(.fr-ca-format) {
        padding-left: 1.6rem;
      }
    }
  }

  .range-selector__radio-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-50);
  }
}
</style>
